import { createContext, useContext, useState, useEffect, useMemo } from 'react';

const TransferTableContext = createContext();

export const TransferTableProvider = ({
    children,
    data = [],
    field,
    filterFunction, // Custom filter function
    initialSearch = '',
    caseSensitive = false,
}) => {
    const [filteredData, setFilteredData] = useState(data);
    const [search, setSearch] = useState(initialSearch);

    const defaultFilterFunction = useMemo(() => {
        return (item, searchValue) => {
            if (!searchValue) return true;

            const fieldValue = item[field];
            if (!fieldValue) return false;

            const itemStr = String(fieldValue);
            const searchStr = String(searchValue);

            return caseSensitive
                ? itemStr.includes(searchStr)
                : itemStr.toLowerCase().includes(searchStr.toLowerCase());
        };
    }, [field, caseSensitive]);

    const filterFn = filterFunction || defaultFilterFunction;

    useEffect(() => {
        if (!search || search.length === 0) {
            setFilteredData(data);
        } else {
            const newFilteredData = data.filter((item) =>
                filterFn(item, search),
            );
            setFilteredData(newFilteredData);
        }
    }, [data, search, filterFn]);

    const handleChangeSearch = (e) => {
        const value = e.target?.value ?? e;
        setSearch(value);
    };

    const clearSearch = () => {
        setSearch('');
    };

    const value = {
        filteredData,
        search,
        setFilteredData,
        handleChangeSearch,
        clearSearch,
        totalCount: data.length,
        filteredCount: filteredData.length,
    };

    return (
        <TransferTableContext.Provider value={value}>
            {children}
        </TransferTableContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTransferTable = () => {
    const context = useContext(TransferTableContext);
    if (!context) {
        throw new Error(
            'useTransferTable must be used within a TransferTableProvider',
        );
    }
    return context;
};
